/**
 * The arithmetic behind the Usage card on an account, kept out of the
 * component so it can be reasoned about — and run — on its own.
 *
 * No value imports, deliberately, on the precedent `analytics-window.ts`,
 * `analytics-charts.ts` and `analytics-users.ts` set: Node 24 strips types, so
 * a module with only type imports and no JSX runs directly under `node` and
 * the self-check at the foot of this file IS its test suite.
 *
 * Two decisions run through all of it, and neither is cosmetic:
 *
 *   * **The two splits are measured in TOKENS, and the sparkline in
 *     REQUESTS.** The route ranks the model breakdown by total tokens and then
 *     CUTS it at its own ceiling, so a browser that re-ranked those rows by
 *     requests would draw "the busiest eight by requests among the heaviest N
 *     by tokens" under a heading claiming the busiest eight. Ranking by the
 *     measure the server ranked by makes the client's cut a prefix of the
 *     server's ordering, which is the only cut that is honest. The
 *     applications list is not cut by anyone, so it is ranked the same way for
 *     consistency rather than out of necessity.
 *   * **Every request is on the chart and zero tokens is a real reading.** An
 *     account whose every request failed carries traffic and no tokens, so a
 *     token split of it is a row of zeros — a picture of nothing, which reads
 *     as a card that failed to load. `allZero` is what lets the card say so
 *     in words instead.
 */

import type { SeriesPoint } from "@empite/ui-components";
import type {
  AnalyticsUserApplication,
  AnalyticsUserDay,
  AnalyticsUserDetail,
  AnalyticsUserModel,
} from "../admin-client";

/**
 * Requests per day, ascending.
 *
 * REQUESTS and not tokens, because the question a sparkline answers is "when
 * was this person active" and a day of failures is a day they were here. The
 * document carries an explicit row for every day of the window at or after
 * `available_from`, so every value here is a measurement and a zero is a
 * measured zero — which is why nothing in this series is ever `null`. The
 * chart primitive would draw a null as a BREAK in the line, and this document
 * has no gaps to break at.
 */
export function userRequestSeries(series: readonly AnalyticsUserDay[]): SeriesPoint[] {
  return series.map((point) => ({ label: point.day, value: point.requests }));
}

/**
 * Whether this account did anything in the window.
 *
 * Deliberately a disjunction over four fields rather than a test of
 * `requests`. The card renders an EmptyState when this is false, and an
 * EmptyState drawn over rows that exist would be a card hiding its own answer
 * — so anything at all that could be drawn counts as usage, and only a
 * document with nothing in any of the four is called quiet.
 *
 * NOT the same question as whether the WINDOW was measured, which is
 * `windowMeasured(available_from, until)` and is asked first. "Nobody had
 * measured yet" and "this person did nothing" are different facts and the card
 * says them in different words.
 */
export function hasUsage(
  detail: Pick<AnalyticsUserDetail, "totals" | "applications" | "models">,
): boolean {
  return (
    detail.totals.requests > 0 ||
    detail.totals.total_tokens > 0 ||
    detail.applications.length > 0 ||
    detail.models.length > 0
  );
}

/**
 * Rank a set of named token counts, biggest first.
 *
 * Ties break on the LABEL, ascending. Nothing in the wire order is guaranteed
 * for two rows on the same figure, and two applications that swap places
 * between two loads of the same page is a chart an operator cannot trust to
 * hold still while they read it.
 */
function rankByTokens(points: SeriesPoint[]): SeriesPoint[] {
  return points.sort(
    (a, b) =>
      (b.value ?? 0) - (a.value ?? 0) || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0),
  );
}

/**
 * One bar per application the account used, measured in tokens.
 *
 * The SLUG is the label rather than the display name: it is what the operator
 * sees everywhere else in this console, it is short enough for a bar's label
 * column, and two applications can carry the same name while no two carry the
 * same slug.
 */
export function applicationParts(
  applications: readonly AnalyticsUserApplication[],
): SeriesPoint[] {
  return rankByTokens(
    applications.map((row) => ({ label: row.application.slug, value: row.total_tokens })),
  );
}

/**
 * One bar per requested ALIAS, measured in tokens, cut at `limit`.
 *
 * `aliases` is the length BEFORE the cut, so the card can say how many rows it
 * is not drawing — a slice with no count beside it reads as the whole list,
 * and the chart's own accessible summary states the total of the BARS.
 *
 * A `limit` of zero means no cut.
 */
export function modelParts(
  models: readonly AnalyticsUserModel[],
  limit: number,
): { points: SeriesPoint[]; aliases: number } {
  const points = rankByTokens(models.map((row) => ({ label: row.alias, value: row.total_tokens })));
  return { points: limit > 0 ? points.slice(0, limit) : points, aliases: points.length };
}

/**
 * Whether a split has rows but nothing to draw.
 *
 * An empty split is not this: it has no rows, and the card says there is
 * nothing to show. This is the narrower case — rows that all measure zero,
 * which happens to an account whose every request failed before a token was
 * counted. The bars would all be zero-length, and a chart of nothing looks
 * exactly like a chart that did not load.
 */
export function allZero(points: readonly SeriesPoint[]): boolean {
  return points.length > 0 && points.every((point) => point.value === 0);
}

/* ------------------------------------------------------------------------ *
 * Self-check: `node apps/admin-portal/src/screens/analytics-user-usage.ts`
 * ------------------------------------------------------------------------ */

/** A day with only the fields a given check reads. The cast is honest at the
 *  one place it is made: these fixtures never reach a renderer. */
function day(fields: Partial<AnalyticsUserDay> & { day: string }): AnalyticsUserDay {
  return {
    requests: 0,
    failed_requests: 0,
    failure_rate: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total_tokens: 0,
    distinct_models_upper_bound: 0,
    distinct_devices_upper_bound: 0,
    ...fields,
  };
}

function app(slug: string, totalTokens: number): AnalyticsUserApplication {
  return {
    application: { id: `app-${slug}`, slug, name: slug },
    first_request_at: null,
    last_request_at: null,
    requests: 0,
    failed_requests: 0,
    failure_rate: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total_tokens: totalTokens,
    distinct_models_upper_bound: 0,
    distinct_devices_upper_bound: 0,
  };
}

function model(alias: string, totalTokens: number): AnalyticsUserModel {
  return {
    alias,
    requests: 0,
    failed_requests: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total_tokens: totalTokens,
    first_request_at: null,
    last_request_at: null,
  };
}

function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    // A replacer, because JSON.stringify DROPS an undefined-valued key and a
    // withheld money field is exactly an undefined-valued key.
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ??
      "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `analytics-user-usage self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  // ---- userRequestSeries ------------------------------------------------
  const series = [
    day({ day: "2026-08-01", requests: 12, total_tokens: 900 }),
    day({ day: "2026-08-02", requests: 0 }),
    day({ day: "2026-08-03", requests: 7, failed_requests: 7 }),
  ];
  check("userRequestSeries carries the day as the label", userRequestSeries(series), [
    { label: "2026-08-01", value: 12 },
    { label: "2026-08-02", value: 0 },
    { label: "2026-08-03", value: 7 },
  ]);
  // The one that would be invisible: a measured zero drawn as a gap would
  // BREAK the line, and a break says "not measured" in this chart's language.
  check("a quiet day is a zero and never a null", userRequestSeries(series)[1]?.value, 0);
  check("userRequestSeries of nothing", userRequestSeries([]), []);
  // Requests and not tokens: a day of nothing but failures still happened.
  check("userRequestSeries reads requests, not tokens", userRequestSeries(series)[2]?.value, 7);

  // ---- hasUsage ---------------------------------------------------------
  const quiet = {
    totals: day({ day: "" }),
    applications: [] as AnalyticsUserApplication[],
    models: [] as AnalyticsUserModel[],
  };
  check("hasUsage on an account that did nothing", hasUsage(quiet), false);
  check(
    "hasUsage on requests alone",
    hasUsage({ ...quiet, totals: day({ day: "", requests: 1 }) }),
    true,
  );
  check(
    "hasUsage on tokens alone",
    hasUsage({ ...quiet, totals: day({ day: "", total_tokens: 1 }) }),
    true,
  );
  // Both list branches matter: a document with rows and a zeroed total is a
  // card that must draw its rows rather than an EmptyState over the top.
  check("hasUsage on an application row alone", hasUsage({ ...quiet, applications: [app("a", 0)] }), true);
  check("hasUsage on a model row alone", hasUsage({ ...quiet, models: [model("cheap", 0)] }), true);

  // ---- applicationParts -------------------------------------------------
  // The two tied rows are planted in REVERSE alphabetical order. `Array.sort`
  // is stable, so a fixture whose ties are already in the right order passes
  // with the tie-break deleted and proves nothing.
  const apps = [app("chat", 10), app("zeta", 400), app("agent", 400)];
  check("applicationParts ranks by tokens, ties by slug", applicationParts(apps), [
    { label: "agent", value: 400 },
    { label: "zeta", value: 400 },
    { label: "chat", value: 10 },
  ]);
  check("applicationParts of nothing", applicationParts([]), []);
  // The label is the slug and not the name — asserted against a fixture whose
  // name differs, so a builder reading `application.name` reports.
  check(
    "applicationParts labels with the slug",
    applicationParts([
      { ...app("chat", 5), application: { id: "x", slug: "chat", name: "Chat Assistant" } },
    ]),
    [{ label: "chat", value: 5 }],
  );

  // ---- modelParts -------------------------------------------------------
  // Tied rows reversed, for the reason `apps` above states.
  const models = [model("smart", 50), model("fast", 900), model("cheap", 900), model("tiny", 1)];
  check("modelParts ranks by tokens, ties by alias", modelParts(models, 0), {
    points: [
      { label: "cheap", value: 900 },
      { label: "fast", value: 900 },
      { label: "smart", value: 50 },
      { label: "tiny", value: 1 },
    ],
    aliases: 4,
  });
  check("modelParts cuts at the limit and counts what it cut", modelParts(models, 2), {
    points: [
      { label: "cheap", value: 900 },
      { label: "fast", value: 900 },
    ],
    aliases: 4,
  });
  // `aliases` is the count BEFORE the cut. Reading it off the cut list would
  // make the card say "0 quieter aliases are not drawn" on every truncation.
  check("modelParts counts before the cut", modelParts(models, 2).aliases, 4);
  check("modelParts of nothing", modelParts([], 8), { points: [], aliases: 0 });
  check("a limit above the length cuts nothing", modelParts(models, 99).points.length, 4);

  // ---- allZero ----------------------------------------------------------
  check("allZero on a split that measured nothing", allZero([{ label: "a", value: 0 }]), true);
  check(
    "allZero with one non-zero part",
    allZero([{ label: "a", value: 0 }, { label: "b", value: 3 }]),
    false,
  );
  // An EMPTY split is a different case with different words, so this must not
  // claim it: "no rows" is answered before "rows that all read zero".
  check("allZero on an empty split", allZero([]), false);

  console.log(`analytics-user-usage self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
